/**
 * Landing v3 — what the agent chat (landing-agent-chat) types: the chat's
 * shortcuts, the requests that type themselves in under them, and the source
 * each request acts on. Data only, so a test can hold every shortcut, request
 * and locale together.
 *
 * "Generate cards" leads (user 2026-09-30): one sentence, typed mid-lecture,
 * that asks for exactly the cards the student wants — the concept described,
 * never the slide it is on — from the lecture's slides. The rest act on the
 * brain-anatomy notes the "sources" tile shows.
 */
import { FlashcardIcon } from '@flashka/shared/ui/icons';

import { BadgeQuestionMark, Layers, NotebookPen, Speech } from 'lucide-react';

/** The chat's shortcuts (the app's own labels and icon hues, `ChatShortcuts`). */
export const AGENT_SHORTCUTS = [
  {
    id: 'generate',
    label: 'chat.shortcuts.generateCards',
    icon: Layers,
    hue: 'text-tertiary-400',
  },
  {
    id: 'edit',
    label: 'chat.shortcuts.editCards',
    icon: FlashcardIcon,
    hue: 'text-tertiary-400',
  },
  {
    id: 'quiz',
    label: 'chat.shortcuts.makeQuiz',
    icon: BadgeQuestionMark,
    hue: 'text-green-400',
  },
  { id: 'oral', label: 'chat.shortcuts.oralExam', icon: Speech, hue: 'text-amber-400' },
  {
    id: 'notes',
    label: 'chat.shortcuts.createNotes',
    icon: NotebookPen,
    hue: 'text-teal-400',
  },
] as const;
export type AgentShortcutId = (typeof AGENT_SHORTCUTS)[number]['id'];

/** The file in the composer: its label's translation key. */
export const AGENT_SOURCES = {
  notes: 'landingV3.tiles.sources.file',
  lecture: 'landingV3.agent.lectureFile',
} as const;
export type AgentSource = keyof typeof AGENT_SOURCES;

/** The requests, in the order they type in (text: `landingV3.agent.prompts.<key>`),
 *  the shortcut each belongs to and the file it acts on. */
export const AGENT_REQUESTS: {
  key: string;
  /** The whole i18n key of its text (greppable). */
  promptKey: string;
  shortcut: AgentShortcutId;
  source: AgentSource;
}[] = [
  {
    key: 'generate',
    promptKey: 'landingV3.agent.prompts.generate',
    shortcut: 'generate',
    source: 'lecture',
  },
  {
    key: 'longer',
    promptKey: 'landingV3.agent.prompts.longer',
    shortcut: 'edit',
    source: 'notes',
  },
  {
    key: 'focus',
    promptKey: 'landingV3.agent.prompts.focus',
    shortcut: 'edit',
    source: 'notes',
  },
  {
    key: 'quiz',
    promptKey: 'landingV3.agent.prompts.quiz',
    shortcut: 'quiz',
    source: 'notes',
  },
  {
    key: 'mnemonic',
    promptKey: 'landingV3.agent.prompts.mnemonic',
    shortcut: 'edit',
    source: 'notes',
  },
  {
    key: 'oral',
    promptKey: 'landingV3.agent.prompts.oral',
    shortcut: 'oral',
    source: 'notes',
  },
  {
    key: 'notes',
    promptKey: 'landingV3.agent.prompts.notes',
    shortcut: 'notes',
    source: 'notes',
  },
];
